import React, { useEffect, useState } from "react";
import he from "he";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";
import { ImgUrl, isProduction } from "@/config";
import { Box, Breadcrumbs, Button, Typography, useMediaQuery, Link as MuiLink, } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ArticleJsonLd, BreadcrumbJsonLd, NextSeo } from "next-seo";
import { seoConfig } from "@/config/seo.config";
import { MetaTag } from "next-seo/lib/types";
import { GetServerSideProps } from "next";
import { GetAllArticleDetail } from "@/api/article";
import { Article } from "@/interfaces/community/article";
import { parseCookies } from "nookies";
import ReactHtmlParser from "react-html-parser";
import Image from "next/image";
import SocialShare from "@/components/share/socialShare";
import ArticlesAll from "@/components/community/article/ArticlesActivities";
import RecArticle from "@/components/community/article/recArticle";
import FacebookTimeline from "@/components/share/socialCard";
import { setTagId } from '@/store/slices/articleActivitySlice';
import ProductRelated from "@/components/community/article/ProductRelated";

interface Props {
  initialActivityDetail: any;
}

// Helper to decode HTML entities
const decodeHTML = (str: string) => {
  let decoded = he.decode(str);
  while (decoded !== str) {
    str = decoded;
    decoded = he.decode(str);
  }
  return decoded;
};

const ActivityDetail: React.FC<Props> = ({ initialActivityDetail }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const isMobile = useMediaQuery("(max-width:600px)");
  const imageShare =
    initialActivityDetail?.socialMediaImageFullPath ||
    `${ImgUrl.pathImg}web/logo-maaboom.webp`;
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  const parsedHtml = ReactHtmlParser(
    initialActivityDetail.articleDetails[0]?.componentValue || ""
  ) as any;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
  };

  const handleTagClick = (tagId: number) => {
    dispatch(setTagId(tagId));
    if (initialActivityDetail.articleCategoryRelations[0].categoryId == 1) {
      router.push(`/community/article`);
    } else {
      router.push(`/community/article`);
    }
  };

  const aspectRatio = 3 / 2; // เปลี่ยนเป็นอัตราส่วนภาพ 3:2 // ตัวอย่างอัตราส่วนภาพ
  const width = 420; // กำหนดความกว้าง container
  const height = width / aspectRatio;

  return (
    <>
      <NextSeo
        title={initialActivityDetail.articleTitle || "บทความ"}
        titleTemplate={`%s | ${seoConfig.title}`}
        defaultTitle={seoConfig.defaultTitle}
        description={
          initialActivityDetail.articleShortDesc || seoConfig.description
        }
        canonical={currentUrl}
        openGraph={{
          ...seoConfig.openGraph,
          url: currentUrl,
          title: `${initialActivityDetail.articleTitle || "บทความ"} | ${
            seoConfig.title
          }`,
          description: `${
            initialActivityDetail.articleShortDesc || seoConfig.description
          }`,
          images: [
            {
              url: imageShare,
              width: 1200,
              height: 630,
              alt: "บทความ",
              type: "image/png",
              secureUrl: imageShare,
            },
          ],
          type: "article",
          article: {
            // publishedTime: initialActivityDetail.publishedDate, // Ensure it's in ISO 8601 format
            // modifiedTime: initialActivityDetail.updatedDate || initialActivityDetail.publishedDate,
            authors: [
              `${initialActivityDetail.writer.writerName}`, // Replace with real author URLs
            ],
            tags: initialActivityDetail.articleTagRelation
            ? initialActivityDetail.articleTagRelation.map((item: any) => item.articleTag.tagTitle)
            : [], // If there are tags in your data
          },
        }}
        additionalMetaTags={
          [
            ...seoConfig.additionalMetaTags,
            isProduction
              ? seoConfig.robots.isProduction
              : seoConfig.robots.isNotProduction,
            {
              name: "twitter:title",
              content: `${initialActivityDetail.articleTitle || "บทความ"} | ${
                seoConfig.title
              }`,
            },
            {
              name: "twitter:description",
              content: `${
                initialActivityDetail.articleShortDesc || seoConfig.description
              }`,
            },
            {
              name: "twitter:image",
              content: imageShare,
            },
          ] as MetaTag[]
        }
        twitter={seoConfig.twitter}
        facebook={seoConfig.facebook}
      />

      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: "หน้าแรก",
            item: "https://maaboom.com",
          },
          {
            position: 2,
            name: "บทความ",
            item: "https://maaboom.com/community/article",
          },
          {
            position: 3,
            name: initialActivityDetail.articleTitle,
            item: currentUrl,
          },
        ]}
      />

      <ArticleJsonLd
        useAppDir={false}
        url={currentUrl} // The current article URL
        title={initialActivityDetail.articleTitle} // The title of the article
        images={[
          `${ImgUrl.imageUrlCenter}uploads/articles/${initialActivityDetail.desktopCoverImage}`, // The image URL
        ]}
        datePublished={initialActivityDetail.publishedDate} // Publish date in ISO 8601 format
        dateModified={
          initialActivityDetail.updatedDate ||
          initialActivityDetail.publishedDate
        } // Use the updated date or fall back to published date
        authorName={[
          {
            name: initialActivityDetail.writer.writerName, // Author's name
            url: initialActivityDetail.writer.WriterImageFullPath, // Author's URL or image
          },
        ]}
        publisherName="maaboom" // Publisher name
        publisherLogo="https://cdn.maaboom.com/web/logos.webp" // Publisher's logo URL
        description={initialActivityDetail.articleShortDesc} // The description of the article
        isAccessibleForFree={true} // Assuming the content is free
      />

      <Grid container spacing={2}>
        <Grid 
          xs={12}
          sx={{ display: { xs: "none", md: "flex" }, mt: 3 }}
        >
          <Image
            src={`${ImgUrl.imageUrlCenter}uploads/articles/${initialActivityDetail.desktopBannerImage}`}
            alt="Article Banner"
            width={1920}
            height={600}
            style={{
              width: "100%",
              height: "auto",
              borderRadius: "8px",
            }}
            loading="lazy"
            placeholder="empty"
            blurDataURL={`${ImgUrl.imageUrlCenter}uploads/articles/${initialActivityDetail.desktopBannerImage}`}
          />
        </Grid>

        <Grid
          xs={12}
          sx={{
            display: { xs: "flex", md: "none", },
            mt: 3,
          }}
        >
          <Image
            src={`${ImgUrl.imageUrlCenter}uploads/articles/${initialActivityDetail.desktopCoverImage}`}
            alt="Article Banner"
            width={width}
            height={height}
            style={{
              width: "100%", // ทำให้ภาพเป็น responsive ตามคอนเทนเนอร์
              height: "auto",
              borderRadius: "8px",
            }}
            loading="lazy"
            placeholder="empty"
            blurDataURL={`${ImgUrl.imageUrlCenter}uploads/articles/${initialActivityDetail.desktopCoverImage}`}
          />
        </Grid>
        
        <Grid xs={12} sm={1} md={1} lg={.7} sx={{ display: { xs: "none", md: "inline-block", lg: "inline-block" } }}>
          <SocialShare data={initialActivityDetail} />
        </Grid>

        <Grid alignItems="flex-start" spacing={2} xs={12} sm={12} md={11} lg={8.3} sx={{ mt: 2 }}>

          <Grid xs={12}>
            <Breadcrumbs aria-label="breadcrumb">
              <MuiLink
                underline="hover"
                color="inherit"
                href="/"
                // onClick={handleClick}
              >
                หน้าแรก
              </MuiLink>
              <MuiLink
                underline="hover"
                color="inherit"
                href="/community/article"
                // onClick={handleClick}
              >
                บทความ
              </MuiLink>
              <Typography color="text.primary">
                {initialActivityDetail.articleTitle}
              </Typography>
            </Breadcrumbs>
          </Grid>

          <Grid xs={12}>
            <Typography
              variant="h1"
              sx={{
                fontSize: "35px",
                my: 4,
                // mb: 3,
                // mt: "12px",
                // fontWeight: "bold",
                fontFamily: "NotoSans-SemiBold, NotoSansThai-SemiBold !important",
              }}
            >
              {initialActivityDetail.articleTitle}
            </Typography>
            {initialActivityDetail?.productRelated.length != 0 && (
              <ProductRelated dataSet={initialActivityDetail?.productRelated}/>
            )}
          </Grid>
          <Grid xs={12} className="article-section" sx={{ my: 3, maxWidth: "100%" }}>{parsedHtml}</Grid>
        </Grid>

        <Grid xs={12} sm={12} md={12} lg={3} sx={{ mt: 2 }}>
          <Typography
            className="txt-bold"
            sx={{ fontSize: "20px", mb: 2, fontWeight: "bold" }}
          >
            ผู้เขียน
          </Typography>
          <Box
            sx={{
              border: "1px solid #dee2e6",
              mt: "10px",
              borderRadius: "14px",
              mb: 4,
            }}
          >
            <Grid
              container
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Image
                src={initialActivityDetail.writer.WriterImageFullPath}
                alt={initialActivityDetail.writer.writerName}
                width={70}
                height={70}
                style={{
                  borderRadius: "50%", // ทำให้ภาพเป็นวงกลม
                  marginLeft: "-9px"
                }}
                loading="lazy"
                placeholder="empty"
                blurDataURL={initialActivityDetail.writer.WriterImageFullPath}
              />
              <Box>
                <Typography
                  className="txt-bold"
                  sx={{
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  By {initialActivityDetail.writer.writerName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#717171",
                  }}
                >
                  {initialActivityDetail.writer.writerDesc}
                </Typography>
              </Box>
            </Grid>
          </Box>
          <Typography className="txt-bold" sx={{ fontSize: "20px", mb: 2 }}>
            Tags
          </Typography>
          <Box
            sx={{
              border: "1px solid #dee2e6",
              mt: "10px",
              p: "15px",
              borderRadius: "14px",
            }}
          >
            {initialActivityDetail.articleTagRelation &&
              initialActivityDetail.articleTagRelation.map((item: any) => (
                <Button
                  key={item.articleTag.id}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: "#ffe0cc",
                    bgcolor: "#ffe0cc",
                    "&:hover": {
                      borderColor: "#fad0b4",
                      bgcolor: "#fad0b4",
                    },
                    color: "#292929",
                    mb: 1,
                    mr: 1,
                    mt: 1,
                    height: "25px",
                  }}
                  onClick={() => {
                    handleTagClick(item.tagId);
                  }}
                >
                  #{item.articleTag.tagTitle}
                </Button>
              ))}
          </Box>
          <FacebookTimeline
            fbId={"https://www.facebook.com/maaboom.official/"}
          />
        </Grid>

        <Grid xs={12} sx={{ mt: 2 }}>
          <Box
            sx={{
              display: {
                xs: "flex",
                md: "none",
              },
            }}
          >
            <SocialShare data={initialActivityDetail} />
          </Box>
          <Typography
            variant="h4"
            align="left"
            className="title-txt txt-bold"
            sx={{ fontWeight: "bold", mb: 2 }}
          >
            <span>maa</span>
            <span>boom</span>
            &nbsp;แนะนำ
          </Typography>
        </Grid>

        {initialActivityDetail.recommendArticle.map((article: Article) => (
          <Grid
            xs={12}
            md={3}
            sm={3}
            lg={3}
            xl={3}
            key={article.id}
            sx={{
              display: {
                xs: "none",
                md: "flex",
                sm: "none",
              },
            }}
          >
            <ArticlesAll dataSet={article} />
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          display: {
            xs: "flex",
            md: "none",
            sm: "flex",
          },
        }}
      >
        <RecArticle initialActivityDetail={initialActivityDetail}/>
      </Box>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const cookies = parseCookies(context);
  const token = cookies.token || "";
  const slug = context.params?.slug as string;

  const responseArticleDetail = await GetAllArticleDetail(token, slug, 4);
  if (!responseArticleDetail || responseArticleDetail?.error) {
    return { redirect: { destination: "/404", permanent: false } };
  }

  const decodedArticleDetail = {
    ...responseArticleDetail,
    articleTitle: decodeHTML(responseArticleDetail.articleTitle),
    articleShortDesc: decodeHTML(
      responseArticleDetail.articleShortDesc.replace(/<\/?[^>]+(>|$)/g, "")
    ),
    articleDetails: responseArticleDetail.articleDetails.map((detail: any) => ({
      ...detail,
      componentValue: decodeHTML(detail.componentValue || ""),
    })),
    recommendArticle: responseArticleDetail.recommendArticle?.map(
      (article: Article) => ({
        ...article,
        articleTitle: decodeHTML(article.articleTitle),
        articleShortDesc: decodeHTML(
          article.articleShortDesc.replace(/<\/?[^>]+(>|$)/g, "")
        ),
      })
    ),
  };

  return { props: { initialActivityDetail: decodedArticleDetail } };
};

export default ActivityDetail;